import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, Share2, Facebook, Instagram, Compass } from "lucide-react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXTwitter, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { Helmet } from "react-helmet";
import { useState } from "react";
import PageBanner from "@/components/PageBanner";
import { trackWhatsAppClick } from "@/lib/analytics";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  image: string;
  date: string;
  readTime: string;
  category: string;
}

export default function Blog() {
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());
  const [imageError, setImageError] = useState(false);

  const toggleLike = (postId: string) => {
    setLikedPosts((prev) => {
      const newLiked = new Set(prev);
      if (newLiked.has(postId)) {
        newLiked.delete(postId);
      } else {
        newLiked.add(postId);
      }
      return newLiked;
    });
  };

  const blogPosts: BlogPost[] = [
    {
      id: "great-migration",
      title:
        "The Great Migration: Understanding the Annual Movement of Wildebeest in East Africa",
      excerpt:
        "Experience the thrill of witnessing the Great Migration, one of Africa's most spectacular wildlife events.",
      content: `Every year, millions of wildebeest, zebras, and gazelles migrate across the Serengeti National Park in Tanzania and the Maasai Mara National Reserve in Kenya.

The Great Migration is driven by the search for food and water. The wildebeest, zebras, and gazelles migrate to areas with abundant grasses and water, which are essential for their survival. This incredible phenomenon is a testament to the awe-inspiring beauty and complexity of East Africa's natural world.

The Great Migration follows a circular route that covers over 1,200 miles. The migration begins in the Serengeti National Park in Tanzania, where the wildebeest, zebras, and gazelles give birth to their young. As the rains end and the grasses dry up, the herds begin their journey northwards to the Maasai Mara National Reserve in Kenya.`,
      image: "/masaimaragreatmigration.png",
      date: "March 18, 2025",
      readTime: "5 min read",
      category: "Wildlife",
    },
    {
      id: "conservation-efforts",
      title:
        "The Importance of Conservation Efforts in East Africa's National Parks and Game Reserves",
      excerpt:
        "Discover the vital conservation efforts protecting East Africa's incredible wildlife and ecosystems.",
      content: `East Africa is home to some of the most incredible wildlife on the planet. The national parks and game reserves of Tanzania, Kenya, and Uganda provide a haven for iconic species such as lions, elephants, and giraffes. However, these ecosystems face numerous threats, from poaching and habitat loss to human-wildlife conflict.

Threats to East Africa's Wildlife
Poaching is a major threat to East Africa's wildlife. Many iconic species, such as elephants and rhinos, are targeted for their valuable body parts. Habitat loss and fragmentation are also major concerns, as human activities such as agriculture and urbanization encroach on wildlife habitats.

Conservation Efforts
Conservation efforts are underway to protect East Africa's wildlife. National parks and game reserves provide a haven for wildlife, and conservation organizations are working to protect and restore habitats. Community-based conservation initiatives are also being implemented, which provide economic benefits to local communities and promote coexistence with wildlife.

Responsible Wildlife Tourism
Responsible wildlife tourism can play a significant role in supporting conservation efforts. By choosing to visit national parks and game reserves, tourists can provide economic benefits to local communities and promote the value of conservation. Our guided tours are designed to promote responsible wildlife tourism practices and support conservation efforts.

Get Involved
There are many ways to get involved in conservation efforts. You can support conservation organizations, spread awareness about the importance of conservation, and make environmentally friendly choices in your daily life. By working together, we can protect East Africa's incredible wildlife and ecosystems for future generations.`,
      image: "/5O5A0035.png",
      date: "March 18, 2025",
      readTime: "6 min read",
      category: "Conservation",
    },
    {
      id: "mobile-photography-tips",
      title: "Essential Mobile Phone Photography Tips for Safari",
      excerpt:
        "Master the art of capturing wildlife moments with your smartphone.",
      content: `
<p><span style="color: green; font-weight: bold;">Mobile Phone Photography Tips</span><br />
Here are simple, effective ways to improve your mobile photography and capture stunning shots with just your phone:</p>

<p><span style="color: green; font-weight: bold;">1. Use Good Lighting</span><br />
Natural light is always the best. Shoot near a window or outside during the golden hour (sunrise or sunset) for soft, flattering light.</p>

<p><span style="color: green; font-weight: bold;">2. Focus on Your Subject</span><br />
Tap on the screen where you want the camera to focus. Your phone will automatically adjust the exposure and create a sharper image.</p>

<p><span style="color: green; font-weight: bold;">3. Use the Rule of Thirds</span><br />
Turn on the grid in your camera settings. Place your subject along the horizontal or vertical lines for a balanced and professional look.</p>

<p><span style="color: green; font-weight: bold;">4. Keep It Steady</span><br />
Hold your phone firmly with both hands or use a tripod or stable surface to avoid blur, especially in low light.</p>

<p><span style="color: green; font-weight: bold;">5. Experiment With Angles</span><br />
Move around. Try shooting from a low angle, overhead, or from the side. Changing your perspective can turn an ordinary shot into a stunning one.</p>

<p><span style="color: green; font-weight: bold;">6. Use the HDR Feature</span><br />
High Dynamic Range (HDR) helps capture more detail in bright and dark areas of your photo. It’s especially useful for landscapes and outdoor scenes.</p>

<p><span style="color: green; font-weight: bold;">7. Edit Your Photos</span><br />
Use built-in editing tools or third-party apps to enhance your photos. Adjust brightness, contrast, sharpness, and color to bring your images to life.</p>
`,

      image: "/traveltips.png",
      date: "March 18, 2025",
      readTime: "4 min read",
      category: "Photography",
    },
    {
      id: "travel-tips",
      title: "Essential Travel Tips for Your African Safari Adventure",
      excerpt:
        "Everything you need to know before embarking on your safari journey.",
      content: `
<p><span style="color: green; font-weight: bold;">Before You Travel</span><br />
1. <b>Check your passport:</b> Ensure it's valid for at least six months beyond your return date.<br />
2. <b>Research, research, research:</b> Learn about your destination's culture, customs, and laws.<br />
3. <b>Book smart:</b> Compare prices, read reviews, and book accommodations and flights in advance.<br />
4. <b>Purchase travel insurance:</b> Protect yourself against unexpected trip cancellations or interruptions.</p>

<p><span style="color: green; font-weight: bold;">Packing Tips</span><br />
1. <b>Pack light:</b> Bring only essentials to avoid luggage fees and hassle.<br />
2. <b>Choose versatile clothing:</b> Pack clothes that can be mixed and matched.<br />
3. <b>Don't forget adapters:</b> Bring power adapters and converters for foreign outlets.<br />
4. <b>Keep valuables safe:</b> Use a money belt or secure bag to protect your belongings.</p>
`,

      image: "/5O5A0010.png",
      date: "March 18, 2025",
      readTime: "6 min read",
      category: "Travel Tips",
    },
    {
      id: "queen-elizabeth-national-park",
      title:
        "Queen Elizabeth National Park: Where the Wild Roams and Lions Climb Trees",
      excerpt:
        "Explore Uganda’s Queen Elizabeth National Park, where vast savannahs, sparkling crater lakes, and the Kazinga Channel teem with elephants, hippos, and rich cultural heritage.",
      content: `In the heart of western Uganda lies Queen Elizabeth National Park, a crown jewel of African safaris. Golden savannahs stretch endlessly, crater lakes glisten under the sun, and the Kazinga Channel winds through the park, alive with hippos and crocodiles. This landscape, rich with wildlife and culture, stands out as one of Africa’s most captivating destinations. <br>

<p><span style="color: green; font-weight: bold;"> Getting There </span><br />
About 400 kilometers southwest of Kampala, the park is easily reached by road. The journey itself offers a preview of what awaits inside, rolling hills, rural homesteads, and the symbolic equator crossing. With every passing mile, anticipation builds toward the vast wilderness of Queen Elizabeth National Park.

<p><span style="color: green; font-weight: bold;"> Why Visit Queen Elizabeth National Park</span><br />
More than just another safari stop, this park offers a complete African wilderness experience. It is home to the Big Four, lion, leopard, elephant, and buffalo, alongside countless other species. The tree-climbing lions of the Ishasha sector make it especially unique, a rare phenomenon found in only two places worldwide.

<p><span style="color: green; font-weight: bold;">Top Attractions & Experiences</span><br />
Game Drives across the Kasenyi Plains and Ishasha sector reveal lions resting in trees, herds of elephants grazing, and antelopes moving gracefully through the grasslands.

<p>Kazinga Channel Boat Cruises provide close encounters with hippos, crocodiles, and elephants cooling off at the banks, while birdlife flourishes along the shoreline.</p>

<p>Bird Watching is exceptional, with over 600 species recorded, including the African fish eagle, shoebill stork, and countless colorful kingfishers.</p>

<p>Crater Lakes such as Lake Katwe and the Bunyaruguru lakes combine geological wonder with stunning viewpoints and cultural insights into salt mining traditions.</p>

<p>Cultural Encounters bring depth to the safari, with opportunities to experience local music, dance, and stories from the communities around the park.</p>

<p><span style="color: green; font-weight: bold;">Practical Information</span><br />
<span style="color: green; font-weight: bold;">Best time to visit:</span> June to September is ideal for game drives during the dry season, though the park remains open year-round.

<p><span style="color: green; font-weight: bold;">What to pack:</span> Light, breathable clothing, sunscreen, insect repellent, binoculars, and a good camera are essentials.</p>

<p><span style="color: green; font-weight: bold;">Entry fees (UWA rates):</span><br />
Foreign non-residents: USD $40 per adult. <br>
Ugandan citizens: UGX 20,000 per adult. 

<p>Guided safaris with experienced operators such as Ecoplace Tours ensure safety, in-depth knowledge, and memorable wildlife encounters.</p>

<p><span style="color: green; font-weight: bold;">Nature’s Living Classroom</span><br />
Queen Elizabeth National Park is a place of constant discovery. A lion draped lazily across a fig tree, a sudden splash of a hippo in the Kazinga Channel, or the quiet grace of elephants moving through the plains, each moment reveals nature’s unpredictability and wonder. It is a reminder that the wilderness holds lessons in patience, awe, and respect for the natural world.

<p><span style="color: green; font-weight: bold;">Recommended For</span><br />
Wildlife Enthusiasts, for those eager to spot the Big Four and the famous tree-climbing lions.

<p>Bird Lovers, with over 600 species, the park is a paradise for birdwatchers.</p>

<p>Photographers, from sunrise over the savannah to elephants at the water’s edge, every moment is a frame-worthy scene.</p>

<p>Adventure Seekers, perfect for those who crave a true African wilderness experience.</p>

<p>Families & Cultural Travelers, blending wildlife encounters with local community experiences makes it enriching for all ages.</p>

<p><span style="color: green; font-weight: bold;">Conclusion</span><br />
With its rare wildlife, stunning scenery, and rich cultural backdrop, Queen Elizabeth National Park delivers an experience that blends adventure with authenticity. Whether for photography, relaxation, or pure exploration, the park remains one of Uganda’s most remarkable destinations. Ecoplace Tours makes these wonders accessible, curating safaris that capture the very spirit of Uganda’s wilderness.`,
      image: "/leopard.webp",
      date: "September 14, 2025",
      readTime: "10 min read",
      category: "Wildlife",
    },
    {
      id: "jinja-Uganda’s-adventure-capital-on-the-nile",
      title:
        "Jinja: Uganda’s Adventure Capital on the Nile",
      excerpt:
        "Discover Jinja, the adventure capital of Uganda, where the mighty Nile River offers thrilling activities amidst stunning landscapes and rich culture.",
      content: `Jinja, perched on the northern shores of Lake Victoria, is where the River Nile begins its 
legendary journey. Just two hours from Kampala, it has earned its title as the adventure capital 
of East Africa, a destination that balances thrill, relaxation, and culture. <br>

Whether you’re planning a corporate retreat, a getaway with friends, or a solo escape, Jinja 
offers something unique for every traveler. 

<p><span style="color: green; font-weight: bold;"> Top Activities in Jinja </span><br />

<p><span style="color: green; font-weight: bold; font-style: italic;">Source of the Nile Tour </span><br />
Stand at the exact point where the world’s longest river begins. A boat ride to the Source of the 
Nile is not just iconic, it’s peaceful, scenic, and unforgettable. 

<p><span style="font-style: italic; ">Best for: Families, couples, cultural explorers.</span><br />

<p><span style="color: green; font-weight: bold;">Zip Lining </span><br />
Fly through the trees with the Nile beneath you. It’s quick, safe, and unforgettable, perfect for 
thrill seekers or group adventures.

<p><span style="font-style: italic; ">Best for: Team building, youth groups, friends on a getaway.</span> <br /> 

<p><span style="color: green; font-weight: bold;">Slide on the Nile </span><br />
Add some laughter to your day with the Slide on the Nile experience, a fun water activity where 
you slip right into the river for the perfect adrenaline splash.
<p><span style="font-style: italic; ">Best for: Team building, friends, families, fun-seekers. </span> <br />

<p><span style="color: green; font-weight: bold;">White-Water Rafting </span><br />
Take on the rapids of the Nile, rated among the best rafting experiences in the world. With 
options ranging from mild to extreme, rafting here is pure adrenaline.

<p><span style="font-style: italic; ">Best for: Team building, adventure seekers, friend getaways.</span> <br />

<p><span style="color: green; font-weight: bold;">Sunset & Boat Cruises </span><br />
When the sun dips, the Nile glows. A sunset cruise offers breathtaking views and relaxation 
after a day of activity.

<p><span style="font-style: italic; ">Best for: Couples, leisure travelers, groups winding down.</span> <br />

<p><span style="color: green; font-weight: bold;">Quad Biking </span><br />
Ride through villages, farmlands, and riverside trails. It’s fast, fun, and gives a closer look at life 
in rural Uganda. 
<p><span style="font-style: italic; ">Best for: Team building, friends, young travelers. </span> <br />

<p><span style="color: green; font-weight: bold;">Horseback Riding </span><br />
Explore the countryside at a slower pace on horseback, guided by professionals. It’s a gentle 
way to connect with Jinja’s landscapes. 
<p><span style="font-style: italic; ">Best for: Families, couples, first-time adventurers. </span> <br />

<p><span style="color: green; font-weight: bold;">Cultural Encounters & Local Markets </span><br />
From crafts to music and local foods, Jinja’s culture is as vibrant as its river. Spend time with the 
communities and taste authentic Ugandan life. 
<p><span style="font-style: italic; ">Best for: Cultural enthusiasts, families, leisure travelers.</span> <br />

<p><span style="color: green; font-weight: bold;">Swimming & Relaxation by the Nile</span><br />
Several lodges and resorts in Jinja offer pools and riverside relaxation spots, making it perfect 
for travelers who prefer a slower pace.
<p><span style="font-style: italic; ">Best for: Families, couples, solo travelers. </span> <br />

<p><span style="color: green; font-weight: bold;">What to Expect from Jinja Activities</span><br />
Visiting Jinja guarantees more than just sightseeing. Here’s what you should look forward to:
<p>Adventure on the water: From world-class rafting to calm sunset cruises.</p>
<p>Bonding opportunities: Perfect settings for team building and group retreats.</p>
<p>Cultural immersion: Local crafts, food, and warm hospitality from communities.</p>
<p>Scenic beauty: Sunsets over the Nile, lush countryside, and endless photography spots.</p>
<p>Flexibility: Whether you’re after high-energy thrills or calm, laid-back experiences, Jinja adapts to your mood. </p>

<p><span style="color: green; font-weight: bold;">Why Jinja Should Be On Your Itinerary</span><br />
Jinja isn’t just a stopover,  it’s a destination that combines adrenaline, relaxation, and culture in a 
way no other place in Uganda does. From corporate groups seeking bonding to couples looking 
for a romantic sunset, it promises memories that stay with you long after you leave. 

<p> At Ecoplace Tours, we design experiences that bring out the very best of Jinja. Whether for a 
team, a family, or a solo explorer, Jinja is a story waiting to be lived.</p>
`,
      image: "/kayaking.png",
      date: "October 22, 2025",
      readTime: "12 min read",
      category: "Adventure",
    },
    {
      id: "the-crane-that-wears-a-crown",
      title:
        "THE CRANE THAT WEARS A CROWN: UGANDA'S SYMBOL OF BEAUTY, UNITY & WILD WONDER",
      excerpt:
        "Explore the story of the Grey Crowned Crane, the majestic bird that became Uganda’s national emblem, and what it still means for travellers today.",
      content: `Explore the story of the Grey Crowned Crane, the majestic bird that became Uganda’s national emblem, and what it still means for travellers today. <br><br>

<p><span style="color: green; font-weight: bold;">A Crown in the Savannah</span><br />
High above Uganda’s wetlands and meadows, among the tall grass and shimmering water, stands a bird both regal and rare. With golden plumes on its head, a red throat sac and piercing eyes, the Grey Crowned Crane moves like one accustomed to reverence. It would become more than just a bird—it would become a national symbol, a marker of Uganda’s identity and beauty.</br>

In the 1920s, under British colonial rule in the Uganda Protectorate, a British officer and ornithologist, Captain Frederick Charles Hood, was charged with designing official badges and symbols. The story goes that he chose this bird—graceful, widely present across Uganda, and matched to the colours of the land—as a symbol of unity, resilience and the natural splendour of the country. Its design was approved by the colonial administration and carried into the new independent nation in 1962.</br></p>

<p><span style="font-style: italic;">More Than a Flag: Meaning & Emotion</span><br />
When you look at the Ugandan flag or coat of arms, you’ll see the Crested Crane standing tall beside the kob (antelope), signifying wildlife, freedom and a vibrant future.</p>

<p><span style="color: green; font-weight: bold;">But what does it mean to travellers and the companies that bring them to these lands?</span><br />
<span style="font-style: italic;">Wonder & Awe:</span> Observing this bird in the wild—its crown shining in golden light, its elegant stance—sparks the kind of wonder that travel lives for.</p>

<p><span style="font-style: italic;">Connection & Belonging:</span><br />
The bird is found across Uganda’s landscapes, in both swamps and grasslands. Travellers share its world for a moment, feeling part of something larger.</p>

<p><span style="font-style: italic;">Pride & Purpose:</span><br />
For Ugandans and visitors alike, seeing the national bird reinforces a sense of heritage, identity and responsibility to protect this wild beauty.</p>

<p><span style="color: green; font-weight: bold;">A Symbol Under Threat</span><br />
The story takes a sobering turn: the crane isn’t just ornamental—it’s endangered. From tens of thousands in past decades, numbers are now as few as 10,000–20,000 in Uganda.<br><br>

Why is this symbol so vulnerable?<br>
Habitat loss: Wetlands drained for agriculture.<br>
Illegal capture: Beliefs and rituals target the bird’s eggs or body parts.<br>
Farming conflict: Cranes forage in fields, creating conflict with farmers.<br>
Legal protection: Uganda enforces strict penalties for harming them (life imprisonment or ~UGX 20 billion fine).</p>

<p>As a traveller with Ecoplace Tours, this story becomes part of your experience. You don’t just visit Uganda’s parks and then leave—you step into a land where symbols live, where wildlife and culture merge, and you become part of that story.</p>

<p>Imagine sitting quietly by a wetland at dawn and hearing the crane’s trumpet call—a sound of country, wildness and life.</p>

<p>Picture yourself on a game drive or boat cruise, spotting the crane among papyrus reeds—a moment where nature mirrors the flag you might have seen in Kampala.</p>

<p>Recognise that your visit supports ecosystems, wetlands, and communities that keep this symbol alive.</p>

<p><span style="color: green; font-weight: bold;">Why This Matters for Ecoplace Tours</span><br />
1. Storytelling Power: The crane gives us a unique narrative linking heritage, wildlife and travel.<br>
2. Meaning-Driven Travel: Our safaris aren’t just about seeing animals—they’re about connecting with the land’s symbols and values.<br>
3. Sustainable Focus: Even if we don’t yet have large sustainability programmes, promoting this bird emphasises that our travel experiences include care for nature and heritage.</p>

<p><span style="color: green; font-weight: bold;">Ready to See the Symbol in Motion?</span><br />
Next time you travel with Ecoplace Tours, keep your eyes open for the golden-crowned crane. Let it be a moment of pause—a connection to Uganda’s heart.</p>

<p>Want to build your next trip around heritage, wildlife and authentic encounters?<br>
Visit www.ecoplacetours.com to explore our itineraries, whether it's bird-watching on a day trip or a multi-day safari into the wild.</p>

<p><span style="font-style: italic;">By Brenda Ayero</span></p>
`,
      image: "/crested-crane.webp",
      date: "November 2025",
      readTime: "10 min read",
      category: "Wildlife",
    }

  ];

  const shareOnSocial = (
    platform: string,
    post: BlogPost,
    e?: React.MouseEvent,
  ) => {
    if (e) {
      e.stopPropagation();
    }
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`${post.title}\n\n${post.excerpt}`);
    const hashtags = encodeURIComponent(
      "SafariTours,WildlifePhotography,AfricanSafari",
    );

    switch (platform) {
      case "facebook":
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
        break;
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?url=${url}&text=${text}&hashtags=${hashtags}`,
        );
        break;
      case "whatsapp":
        window.open(
          `https://wa.me/?text=${text}%20${url}`
        );
        break;
      case "instagram":
        const caption = `${post.title}\n\n${post.excerpt}\n\n#SafariTours #WildlifePhotography #AfricanSafari`;
        window.open(
          `instagram://library?AssetPath=${encodeURIComponent(post.image)}&InstagramCaption=${encodeURIComponent(caption)}`,
        );
        break;
    }
  };

  return (
    <>
      <Helmet>
        <title>Eco Travel Blog - Safari and Wildlife Conservation</title>
        <meta
          name="description"
          content="Discover insights about African wildlife, safari experiences, and conservation efforts."
        />
        <meta
          name="keywords"
          content="safari blog, wildlife conservation, great migration, East Africa, national parks"
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <PageBanner
          title="Eco Travel Blog"
          description="Discover stories about wildlife conservation, safari experiences, and the natural wonders of East Africa"
          backgroundImage="/5O5A9186.JPG"
        />

        <div className="py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              {/* <h1 className="text-4xl font-bold mb-4">Eco Travel Blog</h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Discover stories about wildlife conservation, safari
                experiences, and the natural wonders of East Africa.
              </p>*/}
            </motion.div>

            {selectedPost ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="max-w-4xl mx-auto"
              >
                <Button
                  variant="ghost"
                  onClick={() => setSelectedPost(null)}
                  className="mb-8"
                >
                  ← Back to all posts
                </Button>
                <article>
                  <div className="relative h-[400px] mb-8 rounded-xl overflow-hidden shadow-lg">
                    <img
                      src={selectedPost.image}
                      alt={selectedPost.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        console.error(
                          "Image failed to load:",
                          selectedPost.image,
                        );
                        e.currentTarget.src = "/placeholder-image.jpg";
                      }}
                    />
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <span>{selectedPost.date}</span>
                    <span>•</span>
                    <span>{selectedPost.readTime}</span>
                    <span>•</span>
                    <span>{selectedPost.category}</span>
                  </div>
                  <h1 className="text-4xl font-bold mb-8">
                    {selectedPost.title}
                  </h1>
                  <div
                    className="prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                  />

                  {/* CTA Section */}
                  {/* CTA Section */}
                  <div className="mt-20 relative bg-white rounded-xl shadow-lg border border-neutral-100 overflow-hidden max-w-xl mx-auto">
                    <div className="p-8 text-center bg-white">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-50 text-slate-900 mb-5">
                        <Compass className="w-5 h-5" />
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-3 font-heading tracking-tight">
                        Your Story Starts Here
                      </h3>
                      <p className="text-base text-slate-600 mb-6 max-w-sm mx-auto leading-relaxed">
                        Ready to experience this for yourself? Let's craft your perfect itinerary.
                      </p>
                      <div className="flex flex-col gap-4 justify-center items-center">
                        <Button
                          onClick={() => (window.location.href = "/booking")}
                          className="bg-slate-900 hover:bg-slate-800 text-white text-base px-8 py-6 h-auto w-full sm:w-auto rounded-full font-medium shadow-xl shadow-slate-200 transition-all hover:-translate-y-0.5"
                        >
                          Start Planning Now
                        </Button>
                        <Button
                          onClick={() => {
                            trackWhatsAppClick('Blog Detail CTA');
                            window.open("https://wa.me/256780628299", "_blank");
                          }}
                          className="bg-[#25D366] hover:bg-[#128C7E] text-white text-base px-8 py-6 h-auto w-full sm:w-auto rounded-full font-medium shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className="h-4 w-4 mr-2 fill-current">
                            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zM223.9 438.6c-32.6 0-64.6-9.1-92.9-26.2l-6.6-4-69 18 18.4-67.4-4.3-6.9c-18.3-29.2-28-63.3-28-98.4 0-101.4 82-183.9 184-183.9 49.1 0 95.3 19.1 130 53.9 34.7 34.7 53.9 80.9 53.9 130 0 101.4-82.5 183.9-184.1 183.9zm101.5-138.4c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
                          </svg>
                          Chat with an Expert
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-8 pt-8 border-t">
                    <div className="flex items-center gap-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleLike(selectedPost.id)}
                        className={
                          likedPosts.has(selectedPost.id) ? "text-red-500" : ""
                        }
                      >
                        <Heart
                          className="h-5 w-5"
                          fill={
                            likedPosts.has(selectedPost.id)
                              ? "currentColor"
                              : "none"
                          }
                        />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) =>
                          shareOnSocial("facebook", selectedPost, e)
                        }
                      >
                        <Facebook className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) =>
                          shareOnSocial("twitter", selectedPost, e)
                        }
                      >
                        <FontAwesomeIcon icon={faXTwitter} className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) =>
                          shareOnSocial("whatsapp", selectedPost, e)
                        }
                      >
                        <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) =>
                          shareOnSocial("instagram", selectedPost, e)
                        }
                      >
                        <Instagram className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </article>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {blogPosts.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full flex flex-col overflow-hidden transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl">
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover transform transition-transform duration-300 hover:scale-110"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            console.error("Image failed to load:", post.image);
                            e.currentTarget.src = "/placeholder-image.jpg";
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
                      </div>
                      <CardHeader className="relative z-10">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                          <span>{post.date}</span>
                          <span>•</span>
                          <span>{post.readTime}</span>
                          <span>•</span>
                          <span>{post.category}</span>
                        </div>
                        <CardTitle className="text-2xl hover:text-primary transition-colors">
                          {post.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex-grow">
                        <p className="text-muted-foreground mb-6 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between mt-auto">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedPost(post)}
                            className="hover:bg-primary hover:text-white transition-colors"
                          >
                            Read More
                          </Button>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleLike(post.id);
                              }}
                              className={
                                likedPosts.has(post.id) ? "text-red-500" : ""
                              }
                            >
                              <Heart
                                className="h-5 w-5"
                                fill={
                                  likedPosts.has(post.id)
                                    ? "currentColor"
                                    : "none"
                                }
                              />
                            </Button>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) =>
                                  shareOnSocial("facebook", post, e)
                                }
                              >
                                <Facebook className="h-5 w-5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) =>
                                  shareOnSocial("twitter", post, e)
                                }
                              >
                                <FontAwesomeIcon icon={faXTwitter} className="h-5 w-5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) =>
                                  shareOnSocial("whatsapp", post, e)
                                }
                              >
                                <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) =>
                                  shareOnSocial("instagram", post, e)
                                }
                              >
                                <Instagram className="h-5 w-5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
